import { type KeyboardEvent, useId, useState } from 'react';
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Textarea,
} from '@monksflow/monks-ui';
import { GlobeIcon, XIcon } from 'lucide-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import {
  BRAND_WEBSITES_MAX,
  useAnalysisFormStore,
  type AnalysisFormState,
} from '../../../model/analysisFormStore';
import {
  buildAnalysisProviderOptions,
  DEFAULT_ANALYSIS_PROVIDER,
  findAnalysisModelEntry,
  findAnalysisModelEntryByProvider,
} from '../../../model/modelOptions';
import { useAnalysisStepValidity } from '../../../model/useAnalysisStepValidity';
import { StepShell } from '../shared';

function computeBrandGoalValid(state: AnalysisFormState): boolean {
  return (
    state.brandGoal.brandName.trim() !== '' &&
    state.brandGoal.analysisGoal.trim() !== '' &&
    state.brandGoal.targetMarkets.some((market) => market.trim() !== '')
  );
}

interface BrandGoalStepProps {
  onValidChange?: (isValid: boolean) => void;
}

export const BrandGoalStep = ({ onValidChange }: BrandGoalStepProps) => {
  const { t } = useTranslation('analysis');
  const brandGoal = useAnalysisFormStore((s) => s.brandGoal);
  const setBrandGoal = useAnalysisFormStore((s) => s.setBrandGoal);
  const addBrandWebsite = useAnalysisFormStore((s) => s.addBrandWebsite);
  const removeBrandWebsite = useAnalysisFormStore((s) => s.removeBrandWebsite);
  const model = useAnalysisFormStore((s) => s.model);
  const setModel = useAnalysisFormStore((s) => s.setModel);
  // Rolls out separately from the rest of the step: the redesign can ship while the
  // choice of model stays ours.
  const modelSelectionEnabled = useBooleanFlagValue('iq_model_selection', false);

  const [websiteInput, setWebsiteInput] = useState('');
  const [websiteError, setWebsiteError] = useState<string | null>(null);

  // The API takes a list, so a single market lives in its one slot rather than a second
  // store field that would have to be kept in step with it.
  const targetMarket = brandGoal.targetMarkets[0] ?? '';

  const goalId = useId();
  const reportNameId = useId();
  const brandNameId = useId();
  const websiteId = useId();
  const marketId = useId();
  const modelId = useId();
  const modelLabelId = useId();

  // Each hint is named so its control can point at it. Tessra renders a `FieldDescription`
  // as a paragraph beside the input rather than as part of it, so without this the text is
  // on screen but absent from what a screen reader announces for the field.
  const reportNameHintId = useId();
  const brandNameHintId = useId();
  const websiteHintId = useId();
  const websiteErrorId = useId();
  const marketHintId = useId();
  const modelHintId = useId();

  useAnalysisStepValidity(computeBrandGoalValid, onValidChange);

  const providerOptions = buildAnalysisProviderOptions(t);
  const selectedProvider =
    (model === null ? undefined : findAnalysisModelEntry(model)?.provider) ??
    DEFAULT_ANALYSIS_PROVIDER;

  // The control is provider-level while the API is model-level, so a pick resolves to
  // that provider's model rather than storing the provider itself.
  const handleProviderChange = (provider: string) => {
    const entry = findAnalysisModelEntryByProvider(provider as typeof selectedProvider);
    if (entry) {
      setModel(entry.model);
    }
  };

  /**
   * Accepts `http(s)://` URLs as typed and a `www.` address by assuming https; returns the
   * URL to store, or null for anything else. A bare `brand.com` is refused on purpose.
   */
  const normalizeWebsite = (value: string): string | null => {
    const candidate = /^www\./i.test(value) ? `https://${value}` : value;
    try {
      const { protocol, hostname } = new URL(candidate);
      const isWeb = protocol === 'http:' || protocol === 'https:';
      return isWeb && hostname.includes('.') ? candidate : null;
    } catch {
      return null;
    }
  };

  /**
   * Runs on blur as well as on Add: the wizard unmounts the step on Next, so an
   * input left uncommitted would take the URL with it.
   */
  const addWebsite = () => {
    const value = websiteInput.trim();
    if (!value) {
      setWebsiteError(null);
      return;
    }
    // Refuses what the API would reject anyway, where the error would surface as an
    // unattributed toast at the end of the wizard.
    const website = normalizeWebsite(value);
    if (!website) {
      setWebsiteError(t('analysis.brandGoalStepV2.enterAFullUrl'));
      return;
    }
    if (brandGoal.brandWebsites.length >= BRAND_WEBSITES_MAX) {
      setWebsiteError(t('analysis.brandGoalStepV2.atMostNWebsites', { count: BRAND_WEBSITES_MAX }));
      return;
    }
    addBrandWebsite(website);
    setWebsiteError(null);
    setWebsiteInput('');
  };

  const handleWebsiteKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWebsite();
    }
  };

  return (
    <StepShell
      title={t('analysis.brandGoalStepV2.whatThisIqIsFor')}
      subtitle={t('analysis.brandGoalStepV2.setTheGoalBrandAndTarget')}
    >
      <div className="flex w-full flex-col gap-md">
        <Field>
          <FieldLabel htmlFor={goalId}>
            {t('analysis.brandGoalStepV2.reportGoal')}
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          </FieldLabel>
          <Textarea
            id={goalId}
            required
            rows={4}
            placeholder={t('analysis.brandGoalStepV2.describeYourGoal')}
            value={brandGoal.analysisGoal}
            onChange={(e) => setBrandGoal({ analysisGoal: e.target.value })}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor={reportNameId}>{t('analysis.brandGoalStepV2.reportName')}</FieldLabel>
          <Input
            id={reportNameId}
            aria-describedby={reportNameHintId}
            placeholder={t('analysis.brandGoalStepV2.eGQ3BrandHealth')}
            value={brandGoal.reportName}
            onChange={(e) => setBrandGoal({ reportName: e.target.value })}
          />
          <FieldDescription id={reportNameHintId}>
            {t('analysis.brandGoalStepV2.leaveBlankAndTheBrand')}
          </FieldDescription>
        </Field>

        <Separator />

        <Field>
          <FieldLabel htmlFor={brandNameId}>
            {t('analysis.brandGoalStepV2.brandName')}
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          </FieldLabel>
          <Input
            id={brandNameId}
            required
            aria-describedby={brandNameHintId}
            placeholder={t('analysis.brandGoalStepV2.enterBrand')}
            value={brandGoal.brandName}
            onChange={(e) => setBrandGoal({ brandName: e.target.value })}
          />
          <FieldDescription id={brandNameHintId}>
            {t('analysis.brandGoalStepV2.theBrandOrProduct')}
          </FieldDescription>
        </Field>

        <div className="flex flex-col gap-2">
          <Field>
            <FieldLabel htmlFor={websiteId}>
              {t('analysis.brandGoalStepV2.brandWebsites')}
            </FieldLabel>
            <div className="flex gap-2.5">
              <Input
                id={websiteId}
                type="url"
                aria-describedby={
                  websiteError ? `${websiteHintId} ${websiteErrorId}` : websiteHintId
                }
                className="flex-1"
                placeholder={t('analysis.brandGoalStepV2.httpsYourbrandwebsiteCom')}
                value={websiteInput}
                onChange={(e) => {
                  setWebsiteInput(e.target.value);
                  setWebsiteError(null);
                }}
                onKeyDown={handleWebsiteKeyDown}
                onBlur={addWebsite}
              />
              <Button
                type="button"
                variant="outline"
                onClick={addWebsite}
                disabled={websiteInput.trim() === ''}
              >
                {t('analysis.brandGoalStepV2.add')}
              </Button>
            </div>
            <FieldDescription id={websiteHintId}>
              {t('analysis.brandGoalStepV2.allUrlsWithInformation')}
            </FieldDescription>
            {websiteError ? <FieldError id={websiteErrorId}>{websiteError}</FieldError> : null}
          </Field>
          {brandGoal.brandWebsites.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {brandGoal.brandWebsites.map((website) => (
                <li
                  key={website}
                  className="flex items-center gap-2.5 rounded-lg bg-accent py-1.5 pr-1.5 pl-3"
                >
                  <GlobeIcon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm">{website}</span>
                  <button
                    type="button"
                    aria-label={t('analysis.brandGoalStepV2.removeWebsite', { website })}
                    onClick={() => removeBrandWebsite(website)}
                    className="rounded-full p-1 opacity-70 transition-opacity duration-fast ease-out hover:opacity-100"
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Separator />

        <Field>
          <FieldLabel htmlFor={marketId}>
            {t('analysis.brandGoalStepV2.targetMarket')}
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          </FieldLabel>
          <Input
            id={marketId}
            required
            aria-describedby={marketHintId}
            placeholder={t('analysis.brandGoalStepV2.eGUnitedStates')}
            value={targetMarket}
            onChange={(e) =>
              setBrandGoal({ targetMarkets: e.target.value ? [e.target.value] : [] })
            }
          />
          <FieldDescription id={marketHintId}>
            {t('analysis.brandGoalStepV2.selectTheMarket')}
          </FieldDescription>
        </Field>

        {modelSelectionEnabled && (
          <>
            <Separator />

            <div className="flex flex-col gap-3.5">
              <h3 id={modelLabelId} className="text-sm font-medium text-foreground">
                {t('analysis.brandGoalStepV2.aiModel')}
              </h3>
              <Field>
                <Select value={selectedProvider} onValueChange={handleProviderChange}>
                  <SelectTrigger
                    id={modelId}
                    aria-labelledby={modelLabelId}
                    aria-describedby={modelHintId}
                    data-testid="analysis-model-select"
                  >
                    <SelectValue placeholder={t('analysis.brandGoalStepV2.chooseModel')} />
                  </SelectTrigger>
                  <SelectContent>
                    {providerOptions.map((option) => (
                      <SelectItem key={option.provider} value={option.provider}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldDescription id={modelHintId}>
                  {t('analysis.brandGoalStepV2.selectTheModelUsed')}
                </FieldDescription>
              </Field>
            </div>
          </>
        )}
      </div>
    </StepShell>
  );
};
